import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import {
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import HttpService from "../services/httpService";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  button: {
    marginTop: theme.spacing(2),
  },
  formRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  formItem: {
    flex: 1,
    marginRight: theme.spacing(2),
  },
  formItemLast: {
    flex: 1,
  },
  table: {
    minWidth: 650,
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  createButton: {
    marginBottom: theme.spacing(2),
    float: "right",
  },
  formField: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  tableTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
    fontSize: "1.2rem",
    textAlign: "center",
  },
  tableHeader: {
    backgroundColor: '#1C375D',
    "& .MuiTableCell-head": {
      color: "#fff",
      fontWeight: 600,
    },
  },
  // createButton: {
  //   marginBottom: theme.spacing(4),
  //   fontSize: "1.2rem",
  //   padding: theme.spacing(1.5, 4),
  //   display: "block",
  //   margin: "0 auto",
  // },
}));

const getLastDayOfCurrentMonth = () => {
  return moment().endOf("month").format("YYYY-MM-DD");
};

const getCurrentMonthYear = () => {
  return moment().format("MMYYYY");
};

const OverheadProjectCreation = (props) => {
  const classes = useStyles();
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState({
    projectType: "Overhead",
    projectName: getCurrentMonthYear(),
    completionDate: getLastDayOfCurrentMonth(),
  });
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const validateMMYYYY = (value) => {
    const regex = /^(0[1-9]|1[0-2])\d{4}$/;
    return regex.test(value);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateOverheadProject = async () => {
    try {
      props.setLoading(true);
      const response = await HttpService.createOverheadProject(formData);
      if (response.status === 200) {
        props.setSnackInfo(response.data, "success");
        setOpenForm(false);
        setFormData({
          projectType: "Overhead",
          projectName: getCurrentMonthYear(),
          completionDate: getLastDayOfCurrentMonth(),
        });
      } else if (response.status === 409) {
        props.setSnackInfo(response.data.message, "error");
      }
    } catch (error) {
      console.error("Error creating project:", error.response.status);
      if (error.response.status === 409) {
        props.setSnackInfo(error.response.data.message, "error");
      } else {
        props.setSnackInfo(
          error.message || "Failed to create project",
          "error"
        );
      }
    } finally {
      props.setLoading(false);
    }
  };

  const handleCancel = () => {
    setOpenForm(false);
    setFormData({
      projectType: "Overhead",
      projectName: getCurrentMonthYear(),
      completionDate: null,
    });
  };

  const isFormValid = () => {
    return (
      formData.projectName.trim() !== "" &&
      validateMMYYYY(formData.projectName) &&
      formData.completionDate !== null
    );
  };

  const handleOpenForm = () => {
    setOpenForm(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchProjects(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchProjects(0, newRowsPerPage);
  };

  const fetchProjects = async (pageNum = page, limit = rowsPerPage) => {
    try {
      props.setLoading(true);
      // const response = await HttpService.getOverheadProjects({
      const response = await HttpService.getOverheadProjectsNew({
        pageNum,
        limit,
      });
      // console.log("Response:", response);
      if (response.status === 200) {
        setProjects(response.data.projectsData);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      props.setSnackInfo("Failed to fetch projects", "error");
    } finally {
      props.setLoading(false);
    }
  };

  const handleMarkCompleteClick = (projectId) => {
    setSelectedProjectId(projectId);
    setOpenConfirmDialog(true);
  };

  const handleConfirmComplete = async () => {
    try {
      props.setLoading(true);
      const response = await HttpService.markProjectComplete(selectedProjectId);
      if (response.status === 200) {
        props.setSnackInfo(
          "Project marked as completed successfully",
          "success"
        );
        fetchProjects();
      }
    } catch (error) {
      console.error("Error marking project as complete:", error);
      props.setSnackInfo("Failed to mark project as completed", "error");
    } finally {
      props.setLoading(false);
      setOpenConfirmDialog(false);
      setSelectedProjectId(null);
    }
  };

  const handleCancelComplete = () => {
    setOpenConfirmDialog(false);
    setSelectedProjectId(null);
  };
  useEffect(() => {
    fetchProjects();
  }, []);

  return (
    <div className={classes.root}>
      <Button
        variant="contained"
        color="primary"
        // size="large"
        className={classes.createButton}
        onClick={handleOpenForm}
      >
        Create Project
      </Button>
      <Dialog open={openForm} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogContent>
          <TextField
            className={classes.formField}
            label="Project Type"
            name="projectType"
            value={formData.projectType}
            disabled
            onChange={handleInputChange}
          />
          <TextField
            className={classes.formField}
            label="Project Name (MMYYYY)"
            name="projectName"
            value={formData.projectName}
            onChange={handleInputChange}
            disabled={true}
            error={
              formData.projectName !== "" &&
              !validateMMYYYY(formData.projectName)
            }
            helperText={
              formData.projectName !== "" &&
              !validateMMYYYY(formData.projectName)
                ? "Format should be MMYYYY (e.g., 052025)"
                : ""
            }
            inputProps={{
              placeholder: "052025",
              maxLength: 6,
            }}
          />
          <TextField
            className={classes.formField}
            label="Completion Date"
            type="date"
            name="completionDate"
            value={formData.completionDate || ""}
            onChange={handleInputChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleCreateOverheadProject}
            color="primary"
            variant="contained"
            disabled={!isFormValid()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Typography variant="h6" className={classes.tableTitle}>
        Overhead Projects
      </Typography>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} aria-label="projects table">
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell>S.No.</TableCell>
              <TableCell>Project No</TableCell>
              {/* <TableCell>Experience Center</TableCell> */}
              {/* <TableCell>City</TableCell> */}
              {/* <TableCell>Area</TableCell> */}
              <TableCell>Start Date</TableCell>
              <TableCell>Project Completion</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project, index) => (
              <TableRow key={project.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell component="th">{project.projectNo}</TableCell>
                {/* <TableCell>{project.expCtr}</TableCell> */}
                {/* <TableCell>{project.city}</TableCell> */}
                {/* <TableCell>{project.area}</TableCell> */}
                <TableCell component="th">
                  {project.startDate
                    ? moment(project.startDate).format("DD-MM-YYYY")
                    : "NA"}
                </TableCell>
                <TableCell component="th">
                  {project.projectCompletion
                    ? moment(project.projectCompletion).format("DD-MM-YYYY")
                    : "NA"}
                </TableCell>
                {project.projectCreationStatus !== "Project Under Approval" ? (
                  <TableCell component="th">{`${project.projectCreationStatus} - ${
                    project.projectCreationStatusDate
                      ? moment(project.projectCreationStatusDate).format(
                          "DD-MM-YYYY"
                        )
                      : "NA"
                  }`}</TableCell>
                ) : (
                  <TableCell component="th">{project.projectCreationStatus}</TableCell>
                )}
                <TableCell component="th">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={project.projectCreationStatus !== "Approved"}
                    onClick={() => handleMarkCompleteClick(project.id)}
                  >
                    Mark as Completed
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelComplete}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark this project as completed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelComplete} color="primary">
            No
          </Button>
          <Button
            onClick={handleConfirmComplete}
            color="primary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default connect(null, { setLoading, setSnackInfo })(
  OverheadProjectCreation
);